import React, { useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { useNotesUIStore } from '../../../../store/notesUIStore';
import { cn } from '../../../../lib/utils';

/**
 * MobileNoteSearchInput component
 * 
 * Search input component that integrates with notesUIStore for React Query.
 * All search state is managed through notesUIStore.
 * 
 * Usage:
 * <MobileNoteSearchInput />
 */
interface MobileNoteSearchInputProps {
  className?: string;
  placeholder?: string;
  isMobile?: boolean;
}

export function MobileNoteSearchInput({ 
  className, 
  placeholder = 'Search notes...',
  isMobile = true
}: MobileNoteSearchInputProps) {
  const { activeFilters, setSearchQuery } = useNotesUIStore();
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Clear search query
  const handleClearSearch = () => {
    setSearchQuery('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };
  
  // Update search query
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };
  
  // Check if search is active
  const hasActiveSearch = !!activeFilters.searchQuery?.trim();
  
  // Handle click on the search button/area
  const handleSearchClick = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div
      onClick={handleSearchClick}
      onMouseEnter={() => !isMobile && setIsHovered(true)}
      onMouseLeave={() => !isMobile && setIsHovered(false)}
      className={cn(
        'px-3 py-1.5 rounded-full text-sm flex items-center gap-2',
        'transition-all duration-300 ease-in-out',
        hasActiveSearch || isFocused
          ? 'bg-white text-blue-700 shadow-sm'
          : 'text-gray-700 hover:text-blue-700 hover:bg-white hover:shadow-sm',
        className
      )}
    >
      <Search size={16} className="text-blue-500" />

      <input
        ref={inputRef}
        type="text"
        value={activeFilters.searchQuery}
        onChange={handleSearchChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        className={cn(
          'bg-transparent border-none focus:outline-none focus:ring-0',
          'text-sm h-5 leading-none placeholder-gray-700',
          'transition-all duration-300 ease-in-out',
          (hasActiveSearch || isFocused) ? 'text-blue-700' : 'text-gray-700',
          isMobile
            ? 'w-32'
            : (isHovered || isFocused) ? 'w-32' : 'w-12'
        )}
      />

      <div className="relative">
        {hasActiveSearch && (
          <X
            size={14}
            className="absolute right-0 top-1/2 -translate-y-1/2 text-blue-400 hover:text-blue-600 transition-colors cursor-pointer"
            onClick={e => {
              e.stopPropagation();
              handleClearSearch();
            }}
            aria-label="Clear search"
          />
        )}
      </div>
    </div>
  );
}